import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useReducer,
} from 'react';
import { useTranslation } from 'react-i18next';
import Picker from 'emoji-picker-react';
import ReactGiphySearchbox from 'react-giphy-searchbox';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button } from '../../../UI';
import moment from 'moment';

import {
  LIST_CHATROOM_MESSAGES,
  CREATE_CHAT_MESSAGE,
  MUTE_USER,
  REPORT_USER,
  CREATE_MENTION,
  LIST_CHATROOM_MESSAGES_BY_DATE,
  MARK_CHATROOM_NOTIFICATION_SEEN,
  UPDATE_LAST_MESSAGE_SEEN,
  DELETE_CHAT_MESSAGE,
  UPDATE_CHAT_MESSAGE,
  SEARCH_CHATROOM_MESSAGES,
} from '../../../../http/apollo-queries';
import { Loading } from '../../../UI';
import { Modal } from '../../../Modal';
import { DropdownMenu } from '../../../form';
import { useAuth } from '../../../../context/Auth';
import './chatroom.css';
import MuteModal from './MuteModal';
import ReportModal from './ReportModal';
import DeleteModal from './DeleteModal';
import TaggableTextarea from './TaggableTextarea';
import ImageDropZone from './ImageDropZone';
import {
  ListChatRoomMessagesResponse,
  ChatRoomMessage,
  Mention,
  NotificationMessage,
  MessageTypes,
  ActionTypes,
  InputModes,
} from '../types';
import reducer, { ModalTypes, Action, ReducerState } from './reducer';
import { toast } from 'react-toastify';

const chatFetchLimit = 20;

const initialState = {
  token: {},
  limit: 0,
  messages: [],
  inputMode: { type: InputModes.TEXT },
  gifUrl: null,
  editingMessage: null,
};

const modals: Record<ModalTypes, React.ComponentType<any>> = {
  mute: MuteModal,
  report: ReportModal,
  delete: DeleteModal,
};

interface PropTypes {
  chatRoomId: string;
  title: string;
  setChatId: (id: string) => void;
  notificationMessage?: NotificationMessage;
  messageSearchQuery?: string;
}

const Chatroom = ({
  chatRoomId,
  title,
  setChatId,
  notificationMessage,
  messageSearchQuery,
}: PropTypes) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [saveAttachment, setSaveAttachmentFn] = useState<
    (() => Promise<string | undefined>) | null
  >(null);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);
  const chatHeightRef = useRef<number>(0);
  const isManualScrollRef = useRef<boolean>();

  const { user } = useAuth();

  const [
    {
      token,
      limit,
      anchorMessage,
      messages,
      modalData,
      inputMode,
      gifUrl,
      editingMessage,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const hideModal = () => dispatch({ type: 'DELETE_MODAL_DATA' });

  const resetTextArea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.value = '';
    }
  }, [textareaRef]);

  const focusTextArea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const isInMiddleOfChat = !!anchorMessage;

  const scrollToBottom = useCallback(
    () =>
      setTimeout(() => {
        if (chatRef.current) {
          isManualScrollRef.current = true;
          chatRef.current.scrollBy(0, chatRef.current.scrollHeight);
        }
      }, 1000),
    [chatRef, isManualScrollRef]
  );

  const goToLatestMessage = useCallback(() => {
    dispatch([
      { type: 'RESET_LIMIT' },
      { type: 'DELETE_MESSAGES' },
      { type: 'DELETE_ANCHOR_MESSAGE' },
      { type: 'RESET_TOKEN' },
    ]);
  }, []);

  useEffect(() => {
    if (notificationMessage?.action === ActionTypes.DELETE) {
      dispatch({ type: 'DELETE_MESSAGE', payload: notificationMessage.id });
    } else if (notificationMessage?.action === ActionTypes.CREATE) {
      dispatch([
        { type: 'ADD_MESSAGE', payload: notificationMessage },
        { type: 'DELETE_GIF_URL' },
        {
          type: 'SET_INPUT_MODE',
          payload: {
            type: InputModes.TEXT,
          },
        },
      ]);
      resetTextArea();
      scrollToBottom();
    }
  }, [notificationMessage, scrollToBottom, resetTextArea]);

  useEffect(() => {
    if (messageSearchQuery && chatRoomId) {
      searchChatRoomMessages({
        variables: {
          chatRoomId,
          match: messageSearchQuery,
        },
      });
    }
  }, [messageSearchQuery, chatRoomId]);

  const [updateLastMessageSeen] = useMutation(UPDATE_LAST_MESSAGE_SEEN);

  const makeLastMessageSeen = useCallback(
    (messages: ChatRoomMessage[]) => {
      if (messages.length) {
        const lastMessage = messages[messages.length - 1];

        updateLastMessageSeen({
          variables: {
            chatRoomId,
            id: lastMessage.id,
            sentAt: lastMessage.sentAt,
          },
        });
      }
    },
    [chatRoomId]
  );

  const scrollToPrevPosition = useCallback(() => {
    setTimeout(() => {
      if (chatRef.current) {
        const prevHeight = chatHeightRef.current || 0;
        const currentHeight = chatRef.current.scrollHeight;

        chatRef.current.scrollBy(0, currentHeight - prevHeight);
        chatHeightRef.current = currentHeight;
      }
    }, 500);
  }, [chatHeightRef, chatRef]);

  const cancelEdit = useCallback(() => {
    dispatch({ type: 'DELETE_EDITING_MESSAGE' });
    resetTextArea();
  }, [resetTextArea]);

  const [listChatRoomMessages, { loading: messagesLoading }] =
    useLazyQuery<ListChatRoomMessagesResponse>(LIST_CHATROOM_MESSAGES, {
      onCompleted: (data) => {
        makeLastMessageSeen(data.listChatRoomMessages.items);
      },
    });
  const [listMessagesByDate, { loading: messagesByDateLoading }] = useLazyQuery(
    LIST_CHATROOM_MESSAGES_BY_DATE,
    {
      onCompleted: (data) => {
        makeLastMessageSeen(data.listMessagesByDate.items);
      },
    }
  );
  const [searchChatRoomMessages, { loading: searchMessagesLoading }] =
    useLazyQuery(SEARCH_CHATROOM_MESSAGES, {
      onCompleted: (data) => {
        const { items } = data.searchChatMessages;
        makeLastMessageSeen(items);
        if (items.length) {
          dispatch([
            { type: 'RESET_LIMIT' },
            { type: 'DELETE_MESSAGES' },
            { type: 'RESET_TOKEN' },
            { type: 'SET_ANCHOR_MESSAGE', payload: items[items.length - 1] },
          ]);
        }
      },
    });
  const [deleteMessage] = useMutation(DELETE_CHAT_MESSAGE, {
    onCompleted: (data) => {
      if (data.deleteChatMessage?.id) {
        hideModal();
      }
    },
  });
  const [updateMessage] = useMutation(UPDATE_CHAT_MESSAGE, {
    onCompleted: (data) => {
      if (data.updateChatMessage?.id) {
        dispatch({ type: 'UPDATE_MESSAGE', payload: data.updateChatMessage });
        cancelEdit();
      }
    },
  });
  const [muteUser] = useMutation(MUTE_USER);
  const [reportUser] = useMutation(REPORT_USER);
  const [markNotificationSeen] = useMutation(MARK_CHATROOM_NOTIFICATION_SEEN);
  const [createChatMessage, { loading: createMessageLoading }] =
    useMutation(CREATE_CHAT_MESSAGE);
  const [createMention] = useMutation(CREATE_MENTION, {
    onCompleted: resetTextArea,
  });

  const loading =
    searchMessagesLoading || createMessageLoading || messagesByDateLoading;

  const getChatRoomMessages = useCallback(() => {
    const newLimit = limit + chatFetchLimit;
    const isFirstCall = newLimit === chatFetchLimit;

    if ((token.next || isFirstCall) && !anchorMessage) {
      listChatRoomMessages({
        variables: {
          chatRoomId,
          limit: newLimit,
          nextToken: token.next,
        },
      }).then((response) => {
        dispatch([
          {
            type: 'SET_NEXT_TOKEN',
            payload: response.data?.listChatRoomMessages.nextToken,
          },
          ...(response.data
            ? [
                {
                  type: 'SET_MESSAGES',
                  payload: [
                    ...(response.data.listChatRoomMessages?.items || []),
                  ].reverse(),
                },
                { type: 'SET_LIMIT', payload: newLimit },
              ]
            : ([] as any)),
        ]);

        if (isFirstCall && response.data) {
          scrollToBottom();
        }
      });
    }
  }, [scrollToBottom, limit, token, chatRoomId, anchorMessage]);

  const getChatRoomMessagesByDate = useCallback(
    async (params: { date: string; dir: string }) => {
      const newLimit = limit + chatFetchLimit;
      const isFirstCall = newLimit === chatFetchLimit;
      const nextToken = params.dir === 'next' ? token.next : token.prev;

      if (nextToken || isFirstCall) {
        const response = await listMessagesByDate({
          variables: {
            ...params,
            chatRoomId,
            limit: newLimit,
            sort: params.dir === 'next' ? 'ASC' : 'DESC',
            nextToken,
          },
        });

        dispatch({
          type: `SET_${params.dir.toUpperCase()}_TOKEN` as Action['type'],
          payload: response.data?.listMessagesByDate.nextToken,
        });

        if (response.data) {
          const items = response.data.listMessagesByDate?.items || [];
          dispatch([
            {
              type: 'SET_MESSAGES',
              payload: (prevmessages: typeof messages) => {
                const anchorIndex = prevmessages.findIndex(
                  ({ id }) => id === anchorMessage?.id
                );
                const messagesFirstHalf = prevmessages.slice(0, anchorIndex);
                const messagesSecondHalf = prevmessages.slice(anchorIndex);
                const newMessages =
                  params.dir === 'next'
                    ? [...messagesFirstHalf, ...items]
                    : [...[...items].reverse(), ...messagesSecondHalf];

                return newMessages;
              },
            },
            { type: 'SET_LIMIT', payload: newLimit },
          ]);
        }
      }

      return true;
    },
    [limit, anchorMessage, chatRoomId, token]
  );

  useEffect(() => {
    if (createMessageLoading) {
      scrollToBottom();
    }
  }, [createMessageLoading, scrollToBottom]);

  useEffect(() => {
    if (!messages.length) {
      if (isInMiddleOfChat) {
        // first time load of messages after redirection from notification click
        if (!messagesByDateLoading && anchorMessage.sentAt) {
          if (anchorMessage.type === MessageTypes.MENTION) {
            markNotificationSeen({
              variables: {
                notificationId: anchorMessage.notificationId,
              },
            });
          }
          getChatRoomMessagesByDate({
            date: anchorMessage.sentAt,
            dir: 'next',
          }).then(() => {
            getChatRoomMessagesByDate({
              date: anchorMessage.sentAt,
              dir: 'prev',
            });
          });
        }
      } else if (chatRoomId) {
        getChatRoomMessages();
      }
    } else {
      // update chat height on new messages
      scrollToPrevPosition();
    }
  }, [
    chatRoomId,
    getChatRoomMessages,
    messages.length,
    anchorMessage,
    isInMiddleOfChat,
    getChatRoomMessagesByDate,
    messagesByDateLoading,
    scrollToPrevPosition,
  ]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (
        isManualScrollRef.current === true ||
        isManualScrollRef.current == null ||
        !messages.length ||
        !(anchorMessage ? token.next || token.prev : token.next) ||
        loading
      ) {
        isManualScrollRef.current = false;
        return;
      }

      // scrolled up
      if (event.currentTarget.scrollTop === 0) {
        if (isInMiddleOfChat) {
          getChatRoomMessagesByDate({
            date: anchorMessage.sentAt,
            dir: 'prev',
          });
        } else {
          getChatRoomMessages();
        }
        // scrolled down
      } else if (
        Math.abs(
          event.currentTarget.scrollHeight -
            event.currentTarget.clientHeight -
            event.currentTarget.scrollTop
        ) < 1 &&
        isInMiddleOfChat
      ) {
        getChatRoomMessagesByDate({ date: anchorMessage.sentAt, dir: 'next' });
      }
    },
    [
      isManualScrollRef,
      getChatRoomMessages,
      getChatRoomMessagesByDate,
      token,
      anchorMessage,
      isInMiddleOfChat,
      messages,
      loading,
    ]
  );

  const sendMessage = async () => {
    let type: MessageTypes;

    switch (inputMode.type) {
      case InputModes.GIPHY:
        type = MessageTypes.GIF;
        break;
      case InputModes.IMAGE:
        type = MessageTypes.IMAGE;
        break;
      default:
        type = MessageTypes.MESSAGE;
    }

    let valueObj: { content: string; resource?: string },
      attachmentUrl: string | undefined;

    if (type === MessageTypes.IMAGE && saveAttachment) {
      attachmentUrl = await saveAttachment();

      if (!attachmentUrl) {
        return;
      }

      valueObj = { content: '', resource: attachmentUrl };
    } else {
      const value = gifUrl ?? (textareaRef.current?.value || '').trim();

      if (!value || !chatRoomId) {
        return;
      }

      valueObj =
        type === MessageTypes.GIF
          ? { resource: value, content: '' }
          : { content: value };
    }

    if (editingMessage) {
      await updateMessage({
        variables: {
          chatRoomId,
          ...valueObj,
          messageId: editingMessage,
        },
      });
    } else {
      try {
        if (!mentions.length) {
          await createChatMessage({
            variables: {
              ...valueObj,
              chatRoomId,
              type,
            },
          });
        } else {
          await createMention({
            variables: {
              targetUserIds: mentions.map(({ ownerId }) => ownerId),
              targetUserProfiles: mentions,
              ...valueObj,
              chatRoomId,
            },
          });
        }
      } catch (e: any) {
        if (e.message) {
          toast.warn(t('chatroom.error.profanity'));
        } else {
          toast.error(t('common.error'));
        }
      }
    }

    focusTextArea();
  };

  const handleMuteUser = (data: any) => {
    if (data) {
      muteUser({
        variables: data,
      }).then((response) => {
        if (response.data.muteChatUser.status === 'SUCCESS') {
          hideModal();
        }
      });
    }
  };

  const handleReportUser = (data: any) => {
    if (data) {
      reportUser({
        variables: data,
      });
    }
  };

  const handleTextareaKeyUp: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e
  ) => {
    if (e.code === 'Enter' && !e.shiftKey) {
      sendMessage();
    }
  };

  const handleGiphySelect = (item: any) => {
    dispatch([
      {
        type: 'SET_GIF_URL',
        payload: `https://i.giphy.com/media/${item.id}/giphy.gif`,
      },
      {
        type: 'SET_INPUT_MODE',
        payload: {
          type: InputModes.GIPHY,
          isOpen: false,
        },
      },
    ]);
  };

  const ModalComp = modalData?.type ? modals[modalData.type] : React.Fragment;

  return (
    <div className="h-full">
      <Modal
        onCloseClick={hideModal}
        element={<ModalComp {...(modalData?.data || {})} />}
        isOpen={!!modalData}
      />
      <div className="font-medium py-3 px-4 cursor-pointer">
        <span
          className="material-icons pr-1 align-text-top"
          onClick={() => setChatId('')}
        >
          arrow_back
        </span>
        #{messageSearchQuery && anchorMessage ? messageSearchQuery : title}
      </div>

      <div className="max-h-screen-56 h-full">
        <div
          className={`px-2 ${
            token.next || token.prev ? 'overflow-y-scroll' : 'overflow-y-auto'
          } h-3/5`}
          ref={chatRef}
          onScroll={handleScroll}
        >
          {messagesLoading || messagesByDateLoading ? (
            <Loading className="h-96 m-auto" status={true} />
          ) : (
            messages.map(
              ({
                content,
                resource,
                ownerProfile: { name: ownerName, id: profileId },
                ownerId,
                id,
                sentAt,
                type,
              }) => {
                const isOwnedMessage = ownerId === user?.id;
                const isEditingMessage = editingMessage === id;

                return (
                  <div
                    className={`message-wrapper group dark:hover:text-black py-2 px-2 hover:bg-primary rounded-lg flex justify-between${
                      anchorMessage?.id === id ? ' bg-primary' : ''
                    }`}
                    key={id}
                  >
                    <div>
                      <div>
                        <span className="font-medium text-grey-70 dark:group-hover:text-black">
                          {ownerName}&nbsp;&bull;&nbsp;
                        </span>
                        <span className="text-grey-50">
                          {moment(sentAt).utc().format('DD/MM/YYYY')}
                        </span>
                      </div>
                      <span className="break-all">
                        {type === MessageTypes.GIF ||
                        type === MessageTypes.IMAGE ? (
                          <img alt={'message_res' + id} src={resource} />
                        ) : (
                          content
                        )}
                      </span>
                    </div>
                    {isEditingMessage ? (
                      <Button
                        variant="primary"
                        size="full"
                        buttonClass={'mt-8'}
                        loading={loading}
                        onClick={cancelEdit}
                      >
                        {t('chatroom.cancelEdit')}
                      </Button>
                    ) : (
                      <DropdownMenu
                        isDropdownDisabled={!!modalData}
                        dropdownItems={[
                          ...(!isOwnedMessage
                            ? [
                                <div
                                  key={1}
                                  className="-mx-4 -my-2 p-4 flex-grow-1"
                                  onClick={() =>
                                    dispatch({
                                      type: 'SET_MODAL_DATA',
                                      payload: {
                                        type: ModalTypes.MUTE,
                                        data: {
                                          onClick: () =>
                                            handleMuteUser({
                                              profileId,
                                              userId: ownerId,
                                            }),
                                          name: ownerName,
                                        },
                                      },
                                    })
                                  }
                                >
                                  <div>{t('chatroom.muteUser')}</div>
                                </div>,
                                <div
                                  key={2}
                                  className="-mx-4 -my-2 p-4 flex-grow-1"
                                  onClick={() =>
                                    dispatch({
                                      type: 'SET_MODAL_DATA',
                                      payload: {
                                        type: ModalTypes.REPORT,
                                        data: {
                                          onClick: (text?: string) =>
                                            handleReportUser({
                                              targetUserId: ownerId,
                                              text,
                                            }),
                                          hideModal,
                                          name: ownerName,
                                        },
                                      },
                                    })
                                  }
                                >
                                  <div>{t('chatroom.report')}</div>
                                </div>,
                              ]
                            : []),
                          ...(isOwnedMessage
                            ? [
                                <div
                                  key={3}
                                  className="-mx-4 -my-2 p-4 flex-grow-1"
                                  onClick={() =>
                                    dispatch({
                                      type: 'SET_MODAL_DATA',
                                      payload: {
                                        type: ModalTypes.DELETE,
                                        data: {
                                          onClick: () =>
                                            deleteMessage({
                                              variables: {
                                                chatRoomId,
                                                messageId: id,
                                              },
                                            }),
                                          hideModal,
                                          name: ownerName,
                                        },
                                      },
                                    })
                                  }
                                >
                                  <div>{t('chatroom.delete')}</div>
                                </div>,
                              ]
                            : []),
                          ...((type === MessageTypes.MESSAGE ||
                            type === MessageTypes.MENTION) &&
                          isOwnedMessage
                            ? [
                                <div
                                  key={3}
                                  className="-mx-4 -my-2 p-4 flex-grow-1"
                                  onClick={() => {
                                    dispatch({
                                      type: 'SET_EDITING_MESSAGE',
                                      payload: id,
                                    });
                                    (
                                      textareaRef.current as HTMLTextAreaElement
                                    ).value = content as string;
                                    focusTextArea();
                                  }}
                                >
                                  <div>{t('chatroom.edit')}</div>
                                </div>,
                              ]
                            : []),
                        ]}
                        dropdownClassName="self-center"
                        dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
                      >
                        <span className="more-menu material-icons cursor-pointer hidden">
                          more_vert
                        </span>
                      </DropdownMenu>
                    )}
                  </div>
                );
              }
            )
          )}
          {isInMiddleOfChat && (
            <Button
              variant="primary"
              size="full"
              buttonClass={'mt-8'}
              loading={loading}
              onClick={goToLatestMessage}
            >
              {t('chatroom.toLatestMessage')}
            </Button>
          )}
          <Loading className="pt-4 m-auto" status={createMessageLoading} />
        </div>

        <div className="h-2/5 flex flex-col">
          <div className="flex justify-between border-t-1 border-grey-10 h-1/5 dark:border-grey-80">
            <div className="relative w-10/12 pl-4 border-r-1 border-grey-10 flex items-center dark:border-grey-80">
              <span
                className="material-icons pr-1 text-md align-text-bottom cursor-pointer"
                onClick={() =>
                  dispatch({
                    type: 'SET_INPUT_MODE',
                    payload: (prevMode: ReducerState['inputMode']) =>
                      prevMode.type === InputModes.EMOJI
                        ? { type: InputModes.TEXT }
                        : { type: InputModes.EMOJI, isOpen: true },
                  })
                }
              >
                sentiment_satisfied_alt
              </span>
              <span
                className="material-icons pr-1 text-2xl cursor-pointer align-middle"
                onClick={() =>
                  dispatch({
                    type: 'SET_INPUT_MODE',
                    payload: (prevMode: ReducerState['inputMode']) => {
                      if (prevMode.type === InputModes.GIPHY) {
                        if (gifUrl) {
                          return {
                            type: InputModes.GIPHY,
                            isOpen: !prevMode.isOpen,
                          };
                        }
                        return {
                          type: InputModes.TEXT,
                        };
                      }
                      return {
                        type: InputModes.GIPHY,
                        isOpen: true,
                      };
                    },
                  })
                }
              >
                gif
              </span>
              <span
                className="material-icons pr-1 text-md cursor-pointer align-middle"
                onClick={() =>
                  dispatch({
                    type: 'SET_INPUT_MODE',
                    payload: (prevMode: ReducerState['inputMode']) => {
                      if (prevMode.type === InputModes.IMAGE) {
                        if (gifUrl) {
                          return {
                            type: InputModes.IMAGE,
                            isOpen: !prevMode.isOpen,
                          };
                        }
                        return {
                          type: InputModes.TEXT,
                        };
                      }
                      return {
                        type: InputModes.IMAGE,
                        isOpen: true,
                      };
                    },
                  })
                }
              >
                camera_alt
              </span>
              {inputMode.type === InputModes.GIPHY && inputMode.isOpen && (
                <ReactGiphySearchbox
                  wrapperClassName="absolute bottom-full bg-white border border-grey-10 dark:bg-grey-80 dark:border-grey-70"
                  autoFocus
                  apiKey={process.env.REACT_APP_GIPHY_KEY}
                  onSelect={handleGiphySelect}
                />
              )}
              {inputMode.type === InputModes.EMOJI && (
                <Picker
                  onEmojiClick={(_, emojiObj) => {
                    dispatch({
                      type: 'SET_INPUT_MODE',
                      payload: { type: InputModes.TEXT },
                    });
                    if (textareaRef.current) {
                      textareaRef.current.value += emojiObj.emoji;
                      focusTextArea();
                    }
                  }}
                  pickerStyle={{
                    position: 'absolute',
                    bottom: '100%',
                    zIndex: '1',
                  }}
                />
              )}
            </div>
            <div
              className="w-2/12 py-4 flex justify-center items-center cursor-pointer"
              onClick={sendMessage}
            >
              <svg
                className=""
                width="17"
                height="15"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_10540_99738)">
                  <path
                    d="M12.4668 0.14586C12.3981 0.0785267 12.3113 0.0318376 12.2165 0.0112874C12.1217 -0.00926286 12.0229 -0.00281908 11.9317 0.0298604L0.720138 4.02986C0.62316 4.06448 0.539179 4.12717 0.479306 4.20964C0.419433 4.2921 0.386488 4.39046 0.384831 4.4917C0.383174 4.59293 0.412882 4.69228 0.470026 4.77659C0.527169 4.8609 0.609054 4.9262 0.704849 4.96386L5.59716 6.88486L7.5551 11.6849C7.59286 11.7779 7.65823 11.8577 7.74273 11.9138C7.82723 11.97 7.92698 12 8.02904 11.9999H8.0372C8.14041 11.9983 8.2407 11.966 8.32479 11.9072C8.40887 11.8485 8.47279 11.766 8.50808 11.6709L12.585 0.67086C12.6183 0.581391 12.6249 0.484449 12.6039 0.391441C12.583 0.298434 12.5354 0.213232 12.4668 0.14586Z"
                    fill="#121212"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_10540_99738">
                    <rect
                      width="12.2308"
                      height="12"
                      fill="white"
                      transform="translate(0.384766)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          <div className="px-4 pt-4 border-t-1 border-grey-10 h-4/5 dark:border-grey-8">
            <div className={`h-full${loading ? ' hidden' : ''}`}>
              {(inputMode.type === InputModes.TEXT ||
                inputMode.type === InputModes.EMOJI) && (
                <TaggableTextarea
                  chatRoomId={chatRoomId}
                  mentions={mentions}
                  setMentions={setMentions}
                  autoFocus
                  ref={textareaRef}
                  className={`dark:bg-grey-90 outline-none w-full${
                    loading ? ' hidden' : ''
                  }`}
                  placeholder={t('chatroom.messagePlaceholder')}
                  rows={5}
                  maxLength={300}
                  onKeyUp={handleTextareaKeyUp}
                />
              )}
              {inputMode.type === InputModes.GIPHY && gifUrl && (
                <div className="h-full pb-8 relative">
                  <img alt={'gif'} className="h-full" src={gifUrl} />
                  <span
                    onClick={() => {
                      dispatch([
                        { type: 'DELETE_GIF_URL' },
                        {
                          type: 'SET_INPUT_MODE',
                          payload: {
                            type: InputModes.TEXT,
                          },
                        },
                      ]);
                    }}
                    className="material-icons p-0.5 text-black absolute top-0 right-0 text-2xl cursor-pointer"
                  >
                    close
                  </span>
                </div>
              )}
              {inputMode.type === InputModes.IMAGE && (
                <ImageDropZone setSaveAttachmentFn={setSaveAttachmentFn} />
              )}
            </div>
            <Loading status={loading} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatroom;
